import { Component, inject } from '@angular/core';
import { ActivatedRoute, Router, UrlTree } from '@angular/router';
import { OrderComercioService } from 'src/app/services/order-comercio.service';
import { UsuarioService } from 'src/app/services/usuario.service';
import { Location } from '@angular/common';
import { OrdenComercio } from 'src/app/models/OrdenComercio';
import { ProductoOrdenComercio } from 'src/app/models/ProductoOrdenComercio';
import { VademecumOrdenComercio } from 'src/app/models/VademecumOrdenComercio';
import { EstadoOrden } from 'src/app/models/EstadoOrden';
import { API_URI } from 'src/environments/environment';
import { AuthService } from 'src/app/services/auth.service';
import { LoadingService } from 'src/app/services/loading.service';
import { firstValueFrom } from 'rxjs';


@Component({
    selector: 'app-order-confirm-comercio',
    templateUrl: './order-confirm-comercio.component.html',
    styleUrls: ['./order-confirm-comercio.component.css'],
    standalone: false
})
export class OrderConfirmComercioComponent {
  private _authService = inject(AuthService);
  private _orderComercioService = inject(OrderComercioService);
  private _usuarioService = inject(UsuarioService);
  private _loadingService = inject(LoadingService);
  private router = inject(Router);
  private aRoute = inject(ActivatedRoute);
  private _location = inject(Location);

  error: string = '';
  loading: boolean = false;

  id: number;
  id_perfil_usuario: number;

  ordenComercio?: OrdenComercio;
  productosOrdenComercio: ProductoOrdenComercio[] = [];
  vademecumsOrdenComercio: VademecumOrdenComercio[] = [];

  id_estado_orden?: number;
  preview_img_avatar?: string;
  nombre?: string;
  url_perfil?: string;
  b_envio_domicilio: boolean = false;
  b_pago_mercadopago: boolean = false;
  b_pago_efectivo: boolean = false;
  x_img_receta?: string;
  x_notas: string = '';
  n_precio_envio?: number;
  n_tiempo_envio?: number;
  n_ahorro: number = 0;
  n_total: number = 0;
  estado_orden: string = '';
  private previousUrl?: string;
  urlTree: UrlTree;

  constructor(){
    this.id = Number(this.aRoute.snapshot.paramMap.get('id'));
    this.id_perfil_usuario = this._usuarioService.usuario().id_perfil!;
    this.urlTree = this.router.parseUrl(this.router.url);
    this.previousUrl = this.router.getCurrentNavigation()?.previousNavigation?.finalUrl?.toString();
      
    // --- MercadoPago redirect params ---
    const externalRef = this.urlTree.queryParams['external_reference'];
    const status = this.urlTree.queryParams['status'];

    if (externalRef && status) {
      const idFromMP = Number(externalRef);
      if (!Number.isNaN(idFromMP) && idFromMP > 0) this.id = idFromMP;

      // Actualizar estado según status
      if (status === 'approved') {
        this._orderComercioService.updateEstadoOrdenComercio(this.id, 6).subscribe({ // PAGO APROBADO
          next: () => console.log('Se actualizó Correctamente el Estado de Orden de Comercio (PAGO APROBADO)'),
          error: (e) => console.error('Error al actualizar Estado (approved)', e),
        });
      }

      if (status === 'pending') {
        this._orderComercioService.updateEstadoOrdenComercio(this.id, 7).subscribe({ // PROCESANDO PAGO
          next: () => console.log('Se actualizó Correctamente el Estado de Orden de Comercio (PROCESANDO PAGO)'),
          error: (e) => console.error('Error al actualizar Estado (pending)', e),
        });
      }

      if (status === 'rejected') {
        this._orderComercioService.updateEstadoOrdenComercio(this.id, 8).subscribe({ // PAGO RECAHZADO
          next: () => console.log('Se actualizó Correctamente el Estado de Orden de Comercio (PAGO RECHAZADO)'),
          error: (e) => console.error('Error al actualizar Estado (rejected)', e),
        });
      }
    }
  }

  async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;

    if (!this.id) {
      this.router.navigate(['/dashboard']);
      return;
    }

    await this._loadingService.run(async () => {
      try {
        // Asegura bootstrap de sesión (idempotente, ok en web/nativo)
        await this._authService.ensureSession();
        // 1) Orden
        const orden = await firstValueFrom(this._orderComercioService.getOrdenComercio(this.id));

        // Si no existe la orden
        if (!orden) {
          this.error = 'La orden de comercio  no existe o fue eliminada.';
          this.router.navigate(['/dashboard']);
          return;
        }

        this.ordenComercio = orden;

        this.id_estado_orden = this.ordenComercio.id_estado_orden;
        this.b_envio_domicilio = !!this.ordenComercio.b_envio_domicilio;
        this.b_pago_mercadopago = !!this.ordenComercio.b_pago_mercadopago;
        this.b_pago_efectivo = !!this.ordenComercio.b_pago_efectivo;

        this.x_img_receta = this.ordenComercio.x_img_receta ?? undefined;
        this.x_notas = this.ordenComercio.x_notas ?? '';
        this.n_precio_envio = this.ordenComercio.n_precio_envio ?? 0;
        this.n_tiempo_envio = this.ordenComercio.n_tiempo_envio ?? 0;

        // Estado texto
        const eo = this._orderComercioService.estadosOrdenes.find(
          (x: EstadoOrden) => x.id === this.ordenComercio!.id_estado_orden
        );
        this.estado_orden = eo?.x_descripcion ?? '';

        // Perfil/Avatar según rol
        this.setPerfilHeader();

        // 2) Items
        const [prods, vads] = await Promise.all([
          firstValueFrom(this._orderComercioService.getProductosXOrdenComercio(this.id)),
          firstValueFrom(this._orderComercioService.getVademecumsXOrdenComercio(this.id)),
        ]);

        this.productosOrdenComercio = prods ?? [];
        this.vademecumsOrdenComercio = vads ?? [];

        // 3) Total
        this.refreshTotal();
      } catch (e: any) {
        console.error(e);
        this.error = e?.message || 'No se pudo cargar el detalle del pedido.';
        this.router.navigate(['/sign-in']);
      }
    });
  }

  private setPerfilHeader(): void {
    if (!this.ordenComercio) return;

    const perfil = this._usuarioService.usuario().id_perfil;

    switch (perfil) {
      case 1: // ADMIN
        this.url_perfil = `/perfil-cliente/${this.ordenComercio.id_cliente!}`;
        this.preview_img_avatar = this.ordenComercio.imgAvatarCliente
          ? `${API_URI}uploads/images/small-${this.ordenComercio.imgAvatarCliente}`
          : '';
        this.nombre = this.ordenComercio.nombreCliente ?? '';
        break;

      case 2: // CLIENTE
      case 3: // PRESTADOR
        this.url_perfil = `/perfil-comercio/${this.ordenComercio.id_comercio!}`;
        this.preview_img_avatar = this.ordenComercio.imgAvatarComercio
          ? `${API_URI}uploads/images/small-${this.ordenComercio.imgAvatarComercio}`
          : '';
        this.nombre = this.ordenComercio.nombreComercio ?? '';
        break;

      case 4: // COMERCIO
        this.url_perfil = `/perfil-cliente/${this.ordenComercio.id_cliente!}`;
        this.preview_img_avatar = this.ordenComercio.imgAvatarCliente
          ? `${API_URI}uploads/images/small-${this.ordenComercio.imgAvatarCliente}`
          : '';
        this.nombre = this.ordenComercio.nombreCliente ?? '';
        break;

      default:
        this.url_perfil = '';
        this.preview_img_avatar = '';
        this.nombre = '';
        break;
    }
  }

  // ---- Acciones UI ----
  deleteVademecum(id_vademecum: number) {
    const item = this.vademecumsOrdenComercio.find((x) => x.id_vademecum === id_vademecum);
    if (!item || !item.b_activo) return;

    const qty = item.n_cantidad ?? 1;
    if (qty > 1) item.n_cantidad = qty - 1;
    else item.b_activo = false;

    this.refreshTotal();
  }

  deleteProducto(id_producto: number) {
    const item = this.productosOrdenComercio.find((x) => x.id_producto === id_producto);
    if (!item || !item.b_activo) return;

    const qty = item.n_cantidad ?? 1;
    if (qty > 1) item.n_cantidad = qty - 1;
    else item.b_activo = false;

    this.refreshTotal();
  }

  deleteEnvio() {
    this.n_precio_envio = 0;
    this.n_tiempo_envio = 0;
    this.b_envio_domicilio = false;
    if (this.ordenComercio) this.ordenComercio.b_envio_domicilio = false;
    this.refreshTotal();
  }

  private isActivo(v: any): boolean {
    return v === true || v === 1 || v === '1' || String(v).toLowerCase() === 'true';
  }

  refreshTotal(): void {
    const vads = this.vademecumsOrdenComercio ?? [];
    const prods = this.productosOrdenComercio ?? [];

    // Subtotal (sumo precios finales SOLO de activos)
    const subtotalV = vads
      .filter(v => this.isActivo(v.b_activo))
      .reduce((acc, v) => acc + (Number(v.n_precio_final) || 0) * (Number(v.n_cantidad) || 0), 0);

    const subtotalP = prods
      .filter(p => this.isActivo(p.b_activo))
      .reduce((acc, p) => acc + (Number(p.n_precio_final) || 0) * (Number(p.n_cantidad) || 0), 0);

    // Ahorro (si hay descuento, calculo sobre precio original n_precio)
    const ahorroV = vads
      .filter(v => this.isActivo(v.b_activo) && (Number(v.n_porcentaje_descuento) || 0) > 0)
      .reduce((acc, v) => {
        const precio = Number(v.n_precio) || 0;
        const qty = Number(v.n_cantidad) || 0;
        const desc = Number(v.n_porcentaje_descuento) || 0;
        return acc + (precio * qty * desc / 100);
      }, 0);

    const ahorroP = prods
      .filter(p => this.isActivo(p.b_activo) && (Number(p.n_porcentaje_descuento) || 0) > 0)
      .reduce((acc, p) => {
        const precio = Number(p.n_precio) || 0;
        const qty = Number(p.n_cantidad) || 0;
        const desc = Number(p.n_porcentaje_descuento) || 0;
        return acc + (precio * qty * desc / 100);
      }, 0);

    // Total
    let total = subtotalV + subtotalP;

    if (this.ordenComercio?.b_envio_domicilio && (Number(this.n_precio_envio) || 0) > 0) {
      total += Number(this.n_precio_envio) || 0;
    }

    this.n_total = total;
    this.n_ahorro = ahorroV + ahorroP;
  }

  // ---- MercadoPago / Confirm / Cancel / Entregado ----
  pagarOrderComercio() {
    this.loading = true;
    this.error = '';

    const vadsActivos = this.vademecumsOrdenComercio.filter((v) => v.b_activo);
    const prodsActivos = this.productosOrdenComercio.filter((p) => p.b_activo);

    if (!this.n_total || (vadsActivos.length === 0 && prodsActivos.length === 0)) {
      this.error = 'No puede pagar el pedido porque no tiene productos';
      this.loading = false;
      return;
    }

    if (!this.ordenComercio) {
      this.error = 'No se pudo cargar la orden.';
      this.loading = false;
      return;
    }

    this.ordenComercio.id_estado_orden = 7; // PROCESANDO PAGO

    const ordenComercioUpdate = { ...this.ordenComercio };
    const ordenComercioMP = { ...this.ordenComercio };

    this._orderComercioService.updateOrdenComercio(ordenComercioUpdate, this.vademecumsOrdenComercio, this.productosOrdenComercio).subscribe({
      next: () => {
        this._orderComercioService.pagarMercadoPago(ordenComercioMP, this.vademecumsOrdenComercio, this.productosOrdenComercio).subscribe({
          next: (url) => window.open(url, '_self'),
          error: (e) => {
            this.error = `Error al generar el Pago para MercadoPago en la Orden de Comercio:</br>${e?.error?.message ?? ''}`;
            this.loading = false;
            console.error('Error pagar MP', e);
          },
        });
      },
      error: (e) => {
        this.error = 'No se pudo generar el pago, hubo un error al actualizar la Orden de Comercio';
        this.loading = false;
        console.error('Error update orden (pagar)', e);
      },
      complete: () => console.log('Pagar Orden Comercio Finalizada'),
    });
  }

  confirmarOrderComercio() {
    this.loading = true;
    this.error = '';

    const vadsActivos = this.vademecumsOrdenComercio.filter((v) => v.b_activo);
    const prodsActivos = this.productosOrdenComercio.filter((p) => p.b_activo);

    if (!this.n_total || (vadsActivos.length === 0 && prodsActivos.length === 0)) {
      this.error = 'No puede confirmar el pedido porque no tiene productos';
      this.loading = false;
      return;
    }

    if (!this.ordenComercio) {
      this.error = 'No se pudo cargar la orden.';
      this.loading = false;
      return;
    }

    this.ordenComercio.id_estado_orden = 3; // ACEPTADO

    this._orderComercioService.updateOrdenComercio(this.ordenComercio, this.vademecumsOrdenComercio, this.productosOrdenComercio).subscribe({
      next: () => {
        this._orderComercioService.clearData();
        this.router.navigate(['/dashboard']);
      },
      error: (e) => {
        this.error = `Error al Confirmar la Orden de Comercio:</br>${e?.message ?? ''}`;
        this.loading = false;
        console.error('Error confirmar', e);
      },
    });
  }

  cancelarOrderComercio() {
    this.loading = true;
    this.error = '';

    if (!this.ordenComercio?.id) {
      this.error = 'No se pudo cargar la orden.';
      this.loading = false;
      return;
    }

    this.ordenComercio.id_estado_orden = 5; // CANCELADO

    this._orderComercioService.updateEstadoOrdenComercio(this.ordenComercio.id, 5).subscribe({
      next: () => {
        this._orderComercioService.clearData();
        this.id_estado_orden = 5;
        const eo = this._orderComercioService.estadosOrdenes.find((x: EstadoOrden) => x.id === 5);
        this.estado_orden = eo?.x_descripcion ?? 'Cancelado';
        this.loading = false;
      },
      error: (e) => {
        this.error = `Error al Cancelar la Orden de Comercio:</br>${e?.message ?? ''}`;
        this.loading = false;
        console.error('Error cancelar', e);
      },
    });
  }

  entregadoOrderComercio() {
    this.loading = true;
    this.error = '';

    this._orderComercioService.updateEstadoOrdenComercio(this.id, 4).subscribe({ // COMPLETADO
      next: () => this.router.navigate(['/dashboard']),
      error: (e) => {
        console.error('Error al actualizar el Estado de la Orden de Comercio a COMPLETADO', e);
        this.loading = false;
      },
    });
  }

  // ---- Receta ----
  openFile(): void {
    if (!this.ordenComercio?.x_img_receta) return;
    window.open(`${API_URI}uploads/images/${this.ordenComercio.x_img_receta}`, '_blank');
  }

  // ---- Navegación ----
  back() {
    if (this.previousUrl?.includes('/order-response-comercio/')) {
      this._location.historyGo(-2);
    } else {
      this._location.back();
    }
  }

  openhelp() {
    const el = document.getElementById('tooltip-pwa');
    if (!el) return;

    const showvisible = el.style.display;

    if (showvisible === 'flex') {
      el.style.display = 'none';
      return;
    }

    const help = document.getElementById('helpinfo');
    if (!help) return;

    switch (this.id_perfil_usuario) {
      case 1:
        help.textContent = 'Podrás encontrar los detalles del pedido realizado.';
        break;
      case 2:
        help.textContent = 'Podrás encontrar los detalles del pedido realizado.';
        break;
      case 3:
        help.textContent = 'Podrás encontrar los detalles del pedido realizado.';
        break;
      case 4:
        help.textContent = 'Podrás encontrar los detalles del pedido realizado.';
        break;
      default:
        help.textContent = 'Podrás encontrar los detalles del pedido realizado.';
        break;
    }

    el.style.display = 'flex';
  }
  
}